import { Controller } from "@hotwired/stimulus"
import { detectMobilePlatform } from "../lib/mobile_app_promo_detection.js"

const DISMISSAL_KEY = "newsFeedReader.desktopAppPromo.dismissed"
const ACTION_COUNT_KEY = "newsFeedReader.desktopAppPromo.meaningfulActions"

export default class extends Controller {
  static values = {
    delayMs: { type: Number, default: 45000 },
    dismissDays: { type: Number, default: 14 },
    requiredActions: { type: Number, default: 1 }
  }

  connect() {
    if (this.isMobile() || this.isStandaloneWebApp() || this.isDismissed()) return

    this.elapsed = false
    this.actionCount = this.readSessionNumber(ACTION_COUNT_KEY)
    this.recorded = this.recorded.bind(this)
    window.addEventListener("reader-activity:recorded", this.recorded)
    this.timer = window.setTimeout(() => {
      this.elapsed = true
      this.showIfEligible()
    }, Math.max(0, this.delayMsValue))
  }

  disconnect() {
    window.removeEventListener("reader-activity:recorded", this.recorded)
    if (this.timer) window.clearTimeout(this.timer)
  }

  recorded() {
    this.actionCount = Math.min(100, this.actionCount + 1)
    this.writeSession(ACTION_COUNT_KEY, String(this.actionCount))
    this.showIfEligible()
  }

  dismiss(event) {
    event.preventDefault()
    const expiresAt = Date.now() + Math.max(1, this.dismissDaysValue) * 24 * 60 * 60 * 1000
    this.writeLocal(DISMISSAL_KEY, String(expiresAt))
    this.element.hidden = true
    this.recordProductEvent("mobile_promo.dismissed")
  }

  storeClicked() {
    this.recordProductEvent("mobile_promo.store_clicked")
  }

  showIfEligible() {
    if (!this.elapsed || this.actionCount < Math.max(1, this.requiredActionsValue) || !this.element.hidden) return

    this.element.hidden = false
    this.recordProductEvent("mobile_promo.shown")
  }

  recordProductEvent(event) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) return

    fetch("/product_events", {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ event, destination: "reader", platform: "web" })
    }).catch(() => undefined)
  }

  isMobile() {
    return Boolean(detectMobilePlatform({
      userAgent: navigator.userAgent || "",
      platform: navigator.platform || "",
      maxTouchPoints: navigator.maxTouchPoints || 0
    }))
  }

  isStandaloneWebApp() {
    return window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches === true
  }

  isDismissed() {
    const expiresAt = Number(this.readLocal(DISMISSAL_KEY))
    if (!Number.isFinite(expiresAt)) return false
    if (Date.now() < expiresAt) return true

    this.removeLocal(DISMISSAL_KEY)
    return false
  }

  readSessionNumber(key) {
    const value = Number(this.readSession(key))
    return Number.isFinite(value) ? Math.max(0, value) : 0
  }

  readSession(key) {
    try { return window.sessionStorage?.getItem(key) } catch (_error) { return null }
  }

  writeSession(key, value) {
    try { window.sessionStorage?.setItem(key, value) } catch (_error) { return null }
  }

  readLocal(key) {
    try { return window.localStorage?.getItem(key) } catch (_error) { return null }
  }

  writeLocal(key, value) {
    try { window.localStorage?.setItem(key, value) } catch (_error) { return null }
  }

  removeLocal(key) {
    try { window.localStorage?.removeItem(key) } catch (_error) { return null }
  }
}
